import { useState, useCallback } from 'react';
import dayjs from 'dayjs';
import { validateAnnualWorkingDays, determineRateType } from '../../../utils/rateUtils';
import { roleCopyService } from '../services/RoleCopyService';

/**
 * useRoleCopyOperations Hook
 * 
 * Manages all role copying functionality including individual roles and bulk operations.
 * Extracted from StaffingStep for better maintainability and testability.
 */
export const useRoleCopyOperations = ({
  updateFormData,
  setIsUpdating,
  readOnly,
  saving
}) => {
  // Copy menu state
  const [copyMenuAnchor, setCopyMenuAnchor] = useState(null);
  const [roleToCopy, setRoleToCopy] = useState(null);
  const [copyAllRolesMenuAnchor, setCopyAllRolesMenuAnchor] = useState(null);
  const [sourceMilestoneForCopy, setSourceMilestoneForCopy] = useState(null);

  // Individual role copy handlers
  const handleCopyRoleClick = useCallback((event, role) => {
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
    setCopyMenuAnchor(event.currentTarget);
    setRoleToCopy(role);
  }, []);

  const handleCopyRoleClose = useCallback(() => {
    setCopyMenuAnchor(null);
    setRoleToCopy(null);
  }, []);

  const handleCopyRoleToMilestone = useCallback((targetMilestone) => {
    if (!roleToCopy || readOnly || saving) return;

    setIsUpdating(true);
    const milestoneStart = dayjs(targetMilestone.start_date);
    const milestoneEnd = dayjs(targetMilestone.end_date);

    const milestoneStartCalendarMonth = milestoneStart.month() + 1;
    const milestoneEndCalendarMonth = milestoneEnd.month() + 1;
    const milestoneDuration = Math.max(1, milestoneEnd.diff(milestoneStart, 'month') + 1);

    const copiedRoleWorkingDays = validateAnnualWorkingDays(
      roleToCopy.annualWorkingDays || roleToCopy.effectiveAnnualWorkingDays,
      `copied role ${roleToCopy.name}`
    );

    const copiedRole = {
      ...roleToCopy,
      id: `${targetMilestone.id}-${roleToCopy.name}-${Date.now()}`,
      milestone_id: targetMilestone.id,
      capex_classification: targetMilestone.capex_classification || 'OPEX',
      capex_source: 'milestone',
      milestone_start_month: milestoneStartCalendarMonth,
      milestone_end_month: milestoneEndCalendarMonth,
      overlap_months: milestoneDuration,
      useDefaultTimeline: true,
      dateRanges: [{
        startMonth: milestoneStartCalendarMonth,
        endMonth: milestoneEndCalendarMonth,
        allocation: roleToCopy.allocation || 100,
        timelineVersion: '3.0'
      }],
      defaultAllocation: roleToCopy.allocation || 100,
      timelineVersion: '2.0',
      annualWorkingDays: copiedRoleWorkingDays,
      effectiveAnnualWorkingDays: copiedRoleWorkingDays,
      rateType: determineRateType(copiedRoleWorkingDays)
    };

    updateFormData(prev => ({
      ...prev,
      milestones: prev.milestones.map(milestone => {
        if (milestone.id === targetMilestone.id) {
          return {
            ...milestone,
            roles: [...(milestone.roles || []), copiedRole]
          };
        }
        return milestone;
      })
    }));

    setTimeout(() => setIsUpdating(false), 500);
    handleCopyRoleClose();
    
    return copiedRole; // Return for potential timeline editing
  }, [roleToCopy, readOnly, saving, setIsUpdating, updateFormData, handleCopyRoleClose]);

  // Copy all roles handlers
  const handleCopyAllRolesClick = useCallback((event, milestone) => {
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
    setCopyAllRolesMenuAnchor(event.currentTarget);
    setSourceMilestoneForCopy(milestone);
  }, []);

  const handleCopyAllRolesClose = useCallback(() => {
    setCopyAllRolesMenuAnchor(null);
    setSourceMilestoneForCopy(null);
  }, []);

  const handleCopyAllRolesToMilestone = useCallback((targetMilestone) => {
    if (!sourceMilestoneForCopy || !sourceMilestoneForCopy.roles || 
        sourceMilestoneForCopy.roles.length === 0 || readOnly || saving) {
      handleCopyAllRolesClose();
      return;
    }

    // BACKWARDS COMPATIBILITY: Pre-flight data integrity check using service
    const sourceValidation = roleCopyService.validateDataStructure({ milestones: [sourceMilestoneForCopy] });
    const targetValidation = roleCopyService.validateDataStructure({ milestones: [targetMilestone] });
    
    if (!sourceValidation.isValid || !targetValidation.isValid) {
      console.warn('⚠️ Backwards Compatibility: Data integrity issues detected, but proceeding with safe copy...');
    }

    const sourceRoles = sourceMilestoneForCopy.roles;
    console.log(`🔄 Copying ${sourceRoles.length} roles from "${sourceMilestoneForCopy.name}" to "${targetMilestone.name}"`);
    console.log('🔍 Backwards Compatibility Check: Analyzing source roles for legacy data patterns...');
    
    // BACKWARDS COMPATIBILITY: Log data structure analysis
    sourceRoles.forEach((role, index) => {
      const hasLegacyTimeline = !role.dateRanges || role.dateRanges.length === 0 || 
        role.dateRanges.some(r => !r.timelineVersion || r.timelineVersion !== '3.0');
      const hasLegacyRate = !role.rateAdjustmentInfo && !role.lastRateUpdate;
      const hasLegacyWorkingDays = !role.annualWorkingDays && !role.effectiveAnnualWorkingDays;
      
      if (hasLegacyTimeline || hasLegacyRate || hasLegacyWorkingDays) {
        console.log(`🔍 Role ${index + 1} (${role.name}): Legacy format detected - Timeline: ${hasLegacyTimeline}, Rate: ${hasLegacyRate}, WorkingDays: ${hasLegacyWorkingDays}`);
      }
    });

    setIsUpdating(true);
    
    const targetMilestoneStart = dayjs(targetMilestone.start_date);
    const targetMilestoneEnd = dayjs(targetMilestone.end_date);
    const targetStartCalendarMonth = targetMilestoneStart.month() + 1;
    const targetEndCalendarMonth = targetMilestoneEnd.month() + 1;
    const targetDuration = Math.max(1, targetMilestoneEnd.diff(targetMilestoneStart, 'month') + 1);

    // Create copied roles with backwards compatibility safeguards
    const copiedRoles = sourceRoles.map((sourceRole, index) => {
      // BACKWARDS COMPATIBILITY: Preserve all existing fields
      const preservedFields = { ...sourceRole };
      
      // BACKWARDS COMPATIBILITY: Handle legacy working days
      const legacyWorkingDays = sourceRole.annualWorkingDays || 
                               sourceRole.effectiveAnnualWorkingDays || 
                               (sourceRole.company === 'CA' ? 252 : 260) || 
                               252; // Safe default
      
      const copiedRoleWorkingDays = validateAnnualWorkingDays(
        legacyWorkingDays,
        `copied role ${sourceRole.name || 'Unknown'}`
      );

      // BACKWARDS COMPATIBILITY: Handle legacy timeline formats
      let newDateRanges;
      if (sourceRole.dateRanges && sourceRole.dateRanges.length > 0) {
        // Preserve existing dateRanges structure but update for target milestone
        const firstRange = sourceRole.dateRanges[0];
        const isLegacyFormat = !firstRange.timelineVersion || firstRange.timelineVersion !== '3.0';
        
        console.log(`🔍 Role ${sourceRole.name}: ${isLegacyFormat ? 'Legacy' : 'Modern'} timeline format detected`);
        
        newDateRanges = [{
          startMonth: targetStartCalendarMonth,
          endMonth: targetEndCalendarMonth,
          allocation: firstRange.allocation || sourceRole.allocation || 100,
          timelineVersion: '3.0', // Upgrade to modern format
          // BACKWARDS COMPATIBILITY: Preserve any custom fields from original range
          ...Object.fromEntries(
            Object.entries(firstRange).filter(([key]) => 
              !['startMonth', 'endMonth', 'allocation', 'timelineVersion'].includes(key)
            )
          )
        }];
      } else {
        // BACKWARDS COMPATIBILITY: Create dateRanges for roles that don't have them
        console.log(`🔍 Role ${sourceRole.name}: No dateRanges found, creating from milestone timeline`);
        newDateRanges = [{
          startMonth: targetStartCalendarMonth,
          endMonth: targetEndCalendarMonth,
          allocation: sourceRole.allocation || 100,
          timelineVersion: '3.0'
        }];
      }

      // BACKWARDS COMPATIBILITY: Preserve legacy rate structure
      const legacyRateFields = {
        rate: sourceRole.rate || 0,
        rateAdjustmentInfo: sourceRole.rateAdjustmentInfo || null,
        calculatedForYear: sourceRole.calculatedForYear || null,
        lastRateUpdate: sourceRole.lastRateUpdate || null,
        rateType: sourceRole.rateType || determineRateType(copiedRoleWorkingDays)
      };

      return {
        // BACKWARDS COMPATIBILITY: Start with ALL original fields
        ...preservedFields,
        
        // Override only the fields that MUST change for the new milestone
        id: `${targetMilestone.id}-${sourceRole.name || 'role'}-${Date.now()}-${index}`,
        milestone_id: targetMilestone.id,
        
        // BACKWARDS COMPATIBILITY: Preserve original CAPEX classification unless target overrides
        capex_classification: sourceRole.capex_classification || targetMilestone.capex_classification || 'OPEX',
        capex_source: sourceRole.capex_source || 'milestone',
        
        // Update timeline fields for target milestone
        milestone_start_month: targetStartCalendarMonth,
        milestone_end_month: targetEndCalendarMonth,
        overlap_months: targetDuration,
        
        // BACKWARDS COMPATIBILITY: Preserve timeline preferences
        useDefaultTimeline: sourceRole.useDefaultTimeline !== false, // Default to true if undefined
        dateRanges: newDateRanges,
        defaultAllocation: sourceRole.defaultAllocation || sourceRole.allocation || 100,
        
        // BACKWARDS COMPATIBILITY: Preserve all rate-related fields
        ...legacyRateFields,
        
        // Update timeline version (safe upgrade)
        timelineVersion: '3.0',
        lastTimelineUpdate: new Date().toISOString(),
        
        // BACKWARDS COMPATIBILITY: Only add copiedFrom if it doesn't conflict with existing fields
        ...(sourceRole.copiedFrom ? {} : {
          copiedFrom: {
            milestoneId: sourceMilestoneForCopy.id,
            milestoneName: sourceMilestoneForCopy.name,
            originalRoleId: sourceRole.id,
            copiedAt: new Date().toISOString()
          }
        }),
        
        // BACKWARDS COMPATIBILITY: Preserve working days structure
        annualWorkingDays: copiedRoleWorkingDays,
        effectiveAnnualWorkingDays: sourceRole.effectiveAnnualWorkingDays || copiedRoleWorkingDays
      };
    });
    
    console.log('✅ Backwards Compatibility: All legacy fields preserved, safe upgrades applied');

    // Update form data to add copied roles to target milestone
    updateFormData(prev => ({
      ...prev,
      milestones: prev.milestones.map(milestone => {
        if (milestone.id === targetMilestone.id) {
          return {
            ...milestone,
            roles: [...(milestone.roles || []), ...copiedRoles]
          };
        }
        return milestone;
      })
    }));

    console.log(`✅ Successfully copied ${copiedRoles.length} roles to "${targetMilestone.name}"`);
    
    setTimeout(() => setIsUpdating(false), 500);
    handleCopyAllRolesClose();
    
    // Show success message
    if (typeof window !== 'undefined') {
      const event = new CustomEvent('showNotification', {
        detail: {
          message: `Copied ${copiedRoles.length} roles from "${sourceMilestoneForCopy.name}" to "${targetMilestone.name}"`,
          type: 'success'
        }
      });
      window.dispatchEvent(event);
    }

    return copiedRoles;
  }, [sourceMilestoneForCopy, readOnly, saving, setIsUpdating, updateFormData, 
      handleCopyAllRolesClose]);

  return {
    // State
    copyMenuAnchor,
    roleToCopy,
    copyAllRolesMenuAnchor,
    sourceMilestoneForCopy,
    
    // Individual role copy
    handleCopyRoleClick,
    handleCopyRoleClose,
    handleCopyRoleToMilestone,
    
    // Copy all roles
    handleCopyAllRolesClick,
    handleCopyAllRolesClose,
    handleCopyAllRolesToMilestone
  };
};